import { useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";
import { useBetterbugsMetadata as useCEBetterbugsMetadata } from "ce/hooks/useBetterbugsMetadata";
import {
  PACKAGE_EDITOR_PATH,
  UI_PACKAGE_EDITOR_PATH,
} from "ee/constants/routes/packageRoutes";
import { getIDETypeByUrl } from "ee/entities/IDE/utils";
import { IDE_TYPE } from "ee/IDE/Interfaces/IDETypes";
import { getCurrentModuleId } from "ee/selectors/modulesSelector";
import { getCurrentPackageId } from "ee/selectors/packageSelectors";
import { getCurrentWorkflowId } from "ee/selectors/workflowSelectors";
import type { BetterbugsMetadata } from "utils/Analytics/betterbugs";

/**
 * Inside the Workflow and Package editors, `pageList` still holds the last app
 * opened, so app and page ids are dropped there. Workflow context comes from
 * the route because AI agent apps also set `currentWorkflowId` in the App editor.
 * Package context needs both a package editor route and `currentPackageId`: the
 * Package IDEs clear the id on unmount, but a package engine run that finishes
 * after unmount can set it again, so the route check keeps that stale id out of
 * App editor and Homepage reports.
 */
export const useBetterbugsMetadata = (): BetterbugsMetadata => {
  const ceMetadata = useCEBetterbugsMetadata();
  const { pathname } = useLocation();
  const workflowId = useSelector(getCurrentWorkflowId);
  const packageId = useSelector(getCurrentPackageId);
  const moduleId = useSelector(getCurrentModuleId);

  if (getIDETypeByUrl(pathname) === IDE_TYPE.Workflow) {
    return {
      ...ceMetadata,
      applicationId: null,
      pageId: null,
      workflowId: workflowId || null,
    };
  }

  const isPackageEditorRoute = !!matchPath(pathname, {
    path: [PACKAGE_EDITOR_PATH, UI_PACKAGE_EDITOR_PATH],
  });

  if (isPackageEditorRoute && packageId) {
    return {
      ...ceMetadata,
      applicationId: null,
      pageId: null,
      packageId,
      moduleId: moduleId || null,
    };
  }

  return ceMetadata;
};
